import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS } from '../config/constants';
import { getDifficultySettings } from '../config/difficultySettings';
import { getCurrentPlayer, updatePlayerScore } from '../config/playerManager';

export class GameOverScene extends Phaser.Scene {
  private finalScore: number = 0;
  private isVictory: boolean = false;
  private waveReached: number = 1;
  private starField!: Phaser.GameObjects.Graphics;
  private stars: { x: number; y: number; size: number; alpha: number; speed: number }[] = [];

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: { score: number; victory: boolean; wave: number }): void {
    this.finalScore = data.score || 0;
    this.isVictory = data.victory || false;
    this.waveReached = data.wave || 1;
    
    // Update player stats
    updatePlayerScore(this.finalScore, this.waveReached);
  }

  create(): void {
    // Animated background
    this.createStarField();
    
    // Dark overlay with gradient feel
    this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7);

    // Create celebration/defeat particles
    if (this.isVictory) {
      this.createVictoryEffects();
    } else {
      this.createDefeatEffects();
    }

    // Title glow layer
    const titleText = this.isVictory ? 'VICTORY!' : 'GAME OVER';
    const titleColor = this.isVictory ? '#00ff00' : '#ff0000';
    
    const titleGlow = this.add.text(GAME_WIDTH / 2, 120, titleText, {
      fontFamily: 'Arial Black',
      fontSize: '52px',
      color: titleColor,
    }).setOrigin(0.5).setAlpha(0.4);
    
    const title = this.add.text(GAME_WIDTH / 2, 120, titleText, {
      fontFamily: 'Arial Black',
      fontSize: '48px',
      color: titleColor,
      stroke: '#000000',
      strokeThickness: 6,
    });
    title.setOrigin(0.5);

    // Animate title entrance with bounce
    title.setScale(0);
    titleGlow.setScale(0);
    
    this.tweens.add({
      targets: [title, titleGlow],
      scale: 1,
      duration: 600,
      ease: 'Back.easeOut',
    });

    // Pulsing glow
    this.tweens.add({
      targets: titleGlow,
      alpha: { from: 0.3, to: 0.6 },
      scaleX: { from: 1, to: 1.05 },
      scaleY: { from: 1, to: 1.05 },
      duration: 800,
      yoyo: true,
      repeat: -1,
    });

    // Score display with count-up animation
    this.add.text(GAME_WIDTH / 2, 200, 'FINAL SCORE', {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#00ffff',
    }).setOrigin(0.5).setAlpha(0).setData('delay', 300);

    const scoreText = this.add.text(GAME_WIDTH / 2, 240, '0', {
      fontFamily: 'Arial Black',
      fontSize: '42px',
      color: '#ffff00',
    }).setOrigin(0.5);

    // Animate score counting up
    this.time.delayedCall(400, () => {
      let currentScore = 0;
      const increment = Math.ceil(this.finalScore / 30);
      
      this.time.addEvent({
        delay: 30,
        repeat: 29,
        callback: () => {
          currentScore = Math.min(currentScore + increment, this.finalScore);
          scoreText.setText(currentScore.toString());
        },
      });
    });

    // Fade in score label
    this.children.list.forEach((child) => {
      if (child instanceof Phaser.GameObjects.Text && child.text === 'FINAL SCORE') {
        this.tweens.add({
          targets: child,
          alpha: 1,
          duration: 300,
          delay: 300,
        });
      }
    });

    // Player info and wave
    const player = getCurrentPlayer();
    const difficulty = getDifficultySettings();
    
    this.add.text(GAME_WIDTH / 2, 290, player.name, {
      fontFamily: 'Arial Black',
      fontSize: '20px',
      color: '#00ffff',
    }).setOrigin(0.5);
    
    this.add.text(GAME_WIDTH / 2, 320, `Wave Reached: ${this.waveReached}  |  ${difficulty.name}`, {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);

    // High score with new record indicator
    const isNewRecord = this.finalScore >= player.highScore && this.finalScore > 0;
    
    this.add.text(GAME_WIDTH / 2, 355, `Your High Score: ${player.highScore}`, {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#ff6b35',
    }).setOrigin(0.5);

    if (isNewRecord) {
      const newRecord = this.add.text(GAME_WIDTH / 2, 380, '★ NEW PERSONAL BEST! ★', {
        fontFamily: 'Arial Black',
        fontSize: '18px',
        color: '#ffff00',
      }).setOrigin(0.5);

      this.tweens.add({
        targets: newRecord,
        scale: { from: 1, to: 1.1 },
        alpha: { from: 1, to: 0.7 },
        duration: 500,
        yoyo: true,
        repeat: -1,
      });
    }

    // Play Again button
    this.createButton(GAME_WIDTH / 2, 420, 'PLAY AGAIN', () => {
      this.scene.start('GameScene');
    });

    // Main Menu button
    this.createButton(GAME_WIDTH / 2, 480, 'MAIN MENU', () => {
      this.scene.start('HomeScene');
    });

    // Enable keyboard restart
    this.input.keyboard?.once('keydown-SPACE', () => {
      this.scene.start('GameScene');
    });

    // Hint text
    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 30, 'Press SPACE to play again', {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#666666',
    }).setOrigin(0.5);
  }

  private createButton(x: number, y: number, text: string, callback: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Button glow (behind)
    const glow = this.add.rectangle(0, 0, 210, 50, COLORS.NEON_CYAN, 0.2);
    
    // Button background
    const bg = this.add.rectangle(0, 0, 200, 45, 0x222233)
      .setStrokeStyle(2, COLORS.NEON_CYAN);
    
    // Button text
    const btnText = this.add.text(0, 0, text, {
      fontFamily: 'Arial Black',
      fontSize: '18px',
      color: '#ffffff',
    }).setOrigin(0.5);

    container.add([glow, bg, btnText]);

    // Make interactive
    bg.setInteractive({ useHandCursor: true });
    
    bg.on('pointerover', () => {
      bg.setFillStyle(0x333344);
      bg.setStrokeStyle(3, COLORS.NEON_YELLOW);
      glow.setFillStyle(COLORS.NEON_YELLOW, 0.3);
      btnText.setColor('#ffff00');
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(0x222233);
      bg.setStrokeStyle(2, COLORS.NEON_CYAN);
      glow.setFillStyle(COLORS.NEON_CYAN, 0.2);
      btnText.setColor('#ffffff');
    });

    bg.on('pointerdown', callback);

    return container;
  }

  update(): void {
    this.updateStarField();
  }

  private createStarField(): void {
    this.starField = this.add.graphics();
    this.stars = [];
    
    for (let i = 0; i < 60; i++) {
      this.stars.push({
        x: Phaser.Math.Between(0, GAME_WIDTH),
        y: Phaser.Math.Between(0, GAME_HEIGHT),
        size: Phaser.Math.FloatBetween(0.5, 2),
        alpha: Phaser.Math.FloatBetween(0.2, 0.6),
        speed: Phaser.Math.FloatBetween(0.3, 1),
      });
    }
  }

  private updateStarField(): void {
    this.starField.clear();
    
    this.stars.forEach((star) => {
      star.alpha += Phaser.Math.FloatBetween(-0.02, 0.02);
      star.alpha = Phaser.Math.Clamp(star.alpha, 0.1, 0.8);
      
      star.y += star.speed;
      if (star.y > GAME_HEIGHT) {
        star.y = 0;
        star.x = Phaser.Math.Between(0, GAME_WIDTH);
      }
      
      this.starField.fillStyle(0xffffff, star.alpha);
      this.starField.fillCircle(star.x, star.y, star.size);
    });
  }

  private createVictoryEffects(): void {
    // Confetti-like particles
    const colors = [0xffff00, 0x00ff00, 0x00ffff, 0xff00ff, 0xff6600];
    
    for (let i = 0; i < 50; i++) {
      this.time.delayedCall(i * 50, () => {
        const x = Phaser.Math.Between(50, GAME_WIDTH - 50);
        const color = colors[Phaser.Math.Between(0, colors.length - 1)];
        const particle = this.add.star(x, -20, 4, 4, 8, color, 1);
        
        this.tweens.add({
          targets: particle,
          y: GAME_HEIGHT + 50,
          x: x + Phaser.Math.Between(-100, 100),
          rotation: Math.PI * 4,
          duration: 3000 + Math.random() * 2000,
          onComplete: () => particle.destroy(),
        });
      });
    }

    // Firework bursts
    this.time.addEvent({
      delay: 800,
      repeat: 4,
      callback: () => {
        this.createFirework(
          Phaser.Math.Between(100, GAME_WIDTH - 100),
          Phaser.Math.Between(100, 300)
        );
      },
    });
  }

  private createDefeatEffects(): void {
    // Falling embers
    for (let i = 0; i < 20; i++) {
      this.time.delayedCall(i * 100, () => {
        const x = Phaser.Math.Between(50, GAME_WIDTH - 50);
        const ember = this.add.circle(x, -10, 3, 0xff3300, 0.8);
        
        this.tweens.add({
          targets: ember,
          y: GAME_HEIGHT + 20,
          x: x + Phaser.Math.Between(-50, 50),
          alpha: 0,
          duration: 2000 + Math.random() * 1000,
          onComplete: () => ember.destroy(),
        });
      });
    }
  }

  private createFirework(x: number, y: number): void {
    const colors = [0xffff00, 0x00ffff, 0xff00ff];
    const color = colors[Phaser.Math.Between(0, colors.length - 1)];
    
    // Central flash
    const flash = this.add.circle(x, y, 5, 0xffffff, 1);
    this.tweens.add({
      targets: flash,
      radius: 15,
      alpha: 0,
      duration: 200,
      onComplete: () => flash.destroy(),
    });
    
    // Particles
    for (let i = 0; i < 16; i++) {
      const angle = (i / 16) * Math.PI * 2;
      const distance = 60 + Math.random() * 40;
      const particle = this.add.circle(x, y, 3, color, 1);
      
      this.tweens.add({
        targets: particle,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance + 30,
        alpha: 0,
        scale: 0.3,
        duration: 600 + Math.random() * 200,
        ease: 'Power2',
        onComplete: () => particle.destroy(),
      });
    }
  }
}


